import React, { useEffect, useState } from "react";
import axios from "axios";
import Sidebar from "../components/Sidebar";
import JobDetailsModal from "../components/JobDetailsModal";
import { toast } from "react-hot-toast";

const API_BASE = process.env.REACT_APP_API_BASE;

const ManageJobs = () => {
  const [jobs, setJobs] = useState([]);
  const [filter, setFilter] = useState("all");
  const [selectedJobId, setSelectedJobId] = useState(null);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelJob, setCancelJob] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadJobs = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_BASE}/admin/jobs`);
      setJobs(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("❌ Failed to fetch jobs:", err);
      toast.error("Failed to fetch jobs.");
      setJobs([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadJobs();
  }, []);

  const filteredJobs = jobs.filter((job) => (filter === "all" ? true : job.status === filter));

  const handleCancelJob = async (jobId) => {
    if (!cancelReason.trim()) return toast.error("Please provide a reason.");

    try {
      await axios.delete(`${API_BASE}/admin/delete-job/${jobId}`, {
        data: { reason: cancelReason },
      });

      setJobs((prev) => prev.filter((j) => j._id !== jobId));
      setCancelJob(null);
      setCancelReason("");
      toast.success("✅ Job cancelled successfully!");
    } catch (err) {
      console.error("❌ Error cancelling job:", err);
      toast.error("Failed to cancel job.");
    }
  };

  return (
    <div className="flex font-[Poppins]">
      <Sidebar />
      <main className="ml-0 md:ml-64 p-6 w-full bg-gray-100 min-h-screen">
        <h1 className="text-3xl font-extrabold mb-8 text-gray-800">📋 Manage Jobs</h1>

        <div className="mb-6 flex flex-wrap items-center gap-4">
          <label className="font-semibold text-gray-700">Filter:</label>
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="border px-4 py-2 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All</option>
            <option value="pending">Pending</option>
            <option value="assigned">Assigned</option>
            <option value="worker_completed">Worker Completed</option>
            <option value="completed">Completed</option>
            <option value="dispute">Dispute</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>

        {loading ? (
          <p className="text-gray-600">Loading jobs...</p>
        ) : filteredJobs.length === 0 ? (
          <p className="text-gray-600">No jobs found for this filter.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full bg-white rounded-2xl shadow text-sm">
              <thead className="bg-gray-200 text-gray-700">
                <tr>
                  <th className="py-3 px-4 text-left">Job Title</th>
                  <th className="py-3 px-4 text-left">Customer</th>
                  <th className="py-3 px-4 text-left">Email</th>
                  <th className="py-3 px-4 text-left">City</th>
                  <th className="py-3 px-4 text-left">Budget</th>
                  <th className="py-3 px-4 text-left">Status</th>
                  <th className="py-3 px-4 text-left">Worker</th>
                  <th className="py-3 px-4 text-left">Scheduled</th>
                  <th className="py-3 px-4 text-left">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredJobs.map((job) => (
                  <tr key={job._id} className="border-t hover:bg-gray-50 transition duration-150">
                    <td className="py-3 px-4 font-semibold">{job.jobTitle || "Untitled"}</td>
                    <td className="py-3 px-4">{job.customerName || "N/A"}</td>
                    <td className="py-3 px-4">{job.customerEmail || "N/A"}</td>
                    <td className="py-3 px-4">{job.city || "N/A"}</td>
                    <td className="py-3 px-4">${job.budget ?? 0}</td>
                    <td className="py-3 px-4 capitalize text-indigo-700 font-medium">
                      {(job.status || "").replaceAll("_", " ")}
                    </td>
                    <td className="py-3 px-4">{job.workerName || "—"}</td>
                    <td className="py-3 px-4">
                      {job.scheduledAt ? new Date(job.scheduledAt).toLocaleString() : "Not set"}
                    </td>
                    <td className="py-3 px-4 flex gap-3">
                      <button
                        onClick={() => setSelectedJobId(job._id)}
                        className="text-blue-600 underline hover:text-blue-800 text-sm"
                      >
                        View
                      </button>
                      {job.status !== "cancelled" && (
                        <button
                          onClick={() => setCancelJob(job)}
                          className="text-red-600 underline hover:text-red-800 text-sm"
                        >
                          Cancel
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {selectedJobId && (
          <JobDetailsModal jobId={selectedJobId} onClose={() => setSelectedJobId(null)} />
        )}

        {cancelJob && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white p-6 rounded-xl w-full max-w-md shadow-2xl relative">
              <h2 className="text-xl font-bold text-gray-800 mb-3">Cancel Job – {cancelJob.jobTitle}</h2>
              <p className="text-sm text-gray-700 mb-3">Please provide a reason for cancelling this job.</p>
              <textarea
                rows="4"
                className="w-full border rounded-lg p-3 text-sm focus:ring-2 focus:ring-red-500"
                placeholder="Enter reason..."
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
              />

              <div className="flex justify-end gap-3 mt-4">
                <button
                  onClick={() => setCancelJob(null)}
                  className="px-4 py-2 border rounded-lg text-sm hover:bg-gray-100"
                >
                  Back
                </button>
                <button
                  onClick={() => handleCancelJob(cancelJob._id)}
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm"
                >
                  Confirm Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default ManageJobs;
